import { ZocNode } from "@/core/battle/ai/types/ai.types";
import { UTILITY_CONSTANTS } from "@/core/battle/ai/utils/AIConfig";
import ManagerRegistry from "@/core/battle/ManagerRegistry";
import { HEX_DIRECTIONS, TERRAINS } from "@/core/constants/battle.constants";
import { BattleUnit, Hex, PathInfo, TerrainType } from "@/core/types/battle.types";
import { coordsToId, getAxialNeighbors, idToCoords, getHexDistance } from "@/core/utils/hex.utils";
import { BattleHex } from "@/view/renderers/BattleHex";
import { UnitStack } from "@/view/renderers/UnitStack";

/**
 * Pathfinding calculations
 * Pure utility class with static methods
 */
export class PathCalculator {
    // Static cache - shared globally
    private static pathCache = new Map<string, string[]>();
    
    // Prevent instantiation
    private constructor() {
        throw new Error('PathCalculator is a static utility class');
    }
    
    /**
     * Get reachable nodes from a starting position
     * Uses Dijkstra's algorithm with stamina cost
     */
    public static getReachableNodes(
        start: { q: number, r: number },
        unit: { currentStamina: number, speed: number },
        battleMap: Map<string, Hex>,
        zocMap?: Map<string, ZocNode>  // ← добавить
    ): { 
        reachableIds: string[], 
        reachableMap: Map<string, number>,
        parentMap: Map<string, string | null> // Добавляем карту родителей
    } {
        const BASE_MOVEMENT_COST = 30;
        const visited = new Map<string, number>();
        const parentMap = new Map<string, string | null>(); // Кто привел в этот гекс
        
        const startId = coordsToId(start);

        visited.set(startId, 0);
        parentMap.set(startId, null);

        if (zocMap?.has(startId)) {
            return {
                reachableIds: [startId],
                reachableMap: visited,
                parentMap
            };
        }

        const queue: { q: number, r: number, costSoFar: number }[] = [
            { q: start.q, r: start.r, costSoFar: 0 }
        ];

        while (queue.length > 0) {
            queue.sort((a, b) => a.costSoFar - b.costSoFar);
            const current = queue.shift()!;
            const currentId = coordsToId(current);

            for (const dir of HEX_DIRECTIONS) {
                const nQ = current.q + dir.q;
                const nR = current.r + dir.r;
                const nId = coordsToId({ q: nQ, r: nR });
                const neighbor = battleMap.get(nId);

                if (neighbor && !neighbor.isObstacle && !neighbor.entityId) {
                    const terrainMoveCost = TERRAINS[neighbor.terrain]?.moveCost ?? 10;
                    const stepCost = this.getStepCost(unit.speed, terrainMoveCost);
                    const newTotalCost = current.costSoFar + stepCost;

                    if (newTotalCost <= unit.currentStamina) {
                        if (!visited.has(nId) || newTotalCost < visited.get(nId)!) {
                            visited.set(nId, newTotalCost);
                            parentMap.set(nId, currentId);
                            
                            // ← Если ZoC — добавляем в visited, но не в queue
                            const isZoc = zocMap?.has(nId) ?? false;
                            if (!isZoc) {
                                queue.push({ q: nQ, r: nR, costSoFar: newTotalCost });
                            }
                        }
                    }
                }
            }
        }

        return {
            reachableIds: Array.from(visited.keys()),
            reachableMap: visited,
            parentMap
        };
    }

    public static restorePath(targetId: string, parentMap: Map<string, string | null>): string[] {
        const path: string[] = [];
        let current: string | null = targetId;

        while (current !== null) {
            path.push(current);
            current = parentMap.get(current) || null;
        }

        // Убираем последний элемент (это будет startId), если тебе нужен только путь "куда идти"
        return path.reverse().slice(1); 
    }
    
    /**
     * Find full A* path between two points
     * Cached for performance
     */
    public static findPath(
        startId: string,
        targetId: string,
        battleMap: Map<string, Hex>,
        zocMap?: Map<string, ZocNode>,
    ): string[] {
        // Check cache
        const cacheKey = `${startId}->${targetId}`;
        if (PathCalculator.pathCache.has(cacheKey)) {
            return PathCalculator.pathCache.get(cacheKey)!;
        }
        
        // Compute
        const path = PathCalculator.computeAStarPath(startId, targetId, battleMap, zocMap);
        
        // Cache
        PathCalculator.pathCache.set(cacheKey, path);
        
        return path;
    }
    
    /**
     * Get path considering stamina constraints
     */
    public static getPathToHex(
        startId: string,
        targetId: string,
        reachableHexesWithCost: Map<string, number>,
        battleMap: Map<string, Hex>
    ): PathInfo {
        const fullPath = PathCalculator.findPath(startId, targetId, battleMap);
        
        if (fullPath.length === 0) {
            return { path: [], lastStep: null, cost: 0 };
        }

        const validPath: string[] = [];
        let lastStepId: string | null = null;
        
        let totalCost = 0;
        
        for (const stepId of fullPath) {
            if (reachableHexesWithCost.has(stepId)) {
                validPath.push(stepId);
                lastStepId = stepId;
                totalCost = reachableHexesWithCost.get(stepId) || totalCost;
            } else {
                break;
            }
        }

        let lastStep: {id: string, terrain: TerrainType} | null = {id: '', terrain: TerrainType.Plains};

        if (lastStepId) {
            const lastHex = battleMap.get(lastStepId);
            if (lastHex) {
                lastStep.id = lastStepId;
                lastStep.terrain = lastHex.terrain
            }
        } else {
            lastStep = null;
        }

        return { path: validPath, lastStep, cost: totalCost };
    }

    public static constructFullPath(startPos: {x: number, y: number}, pathIds: string[], hexes: Map<string, BattleHex>): Array<{ x: number; y: number }> {
        const path = [{ x: startPos.x, y: startPos.y }];
        pathIds.forEach(id => {
            const h = hexes.get(id);
            if (h) path.push({ x: h.x, y: h.y });
        });
        return path;
    }

    public static getPathZocHexes(
        unit: BattleUnit,
        path: string[],
        enemies: BattleUnit[]
    ): Set<string> {
        if (path.length < 1) return new Set<string>();
        const zocHexes = new Set<string>();
        const fullPath = [coordsToId(unit), ...path.slice(0, -1)];

        for (const enemy of enemies) {
            if (enemy.isDead) continue;
            if (enemy.opportunityAttackUsed) continue;
            
            const neighbors = getAxialNeighbors(enemy.q, enemy.r);
            for (const n of neighbors) {
                const nId = coordsToId(n);
                if (fullPath.includes(nId)) {
                    zocHexes.add(nId);
                }
            }
        }

        return zocHexes;
    }
    
    /**
     * Calculate radius around center point
     */
    public static getRadius(
        center: { q: number, r: number },
        radius: number,
        battleMap: Map<string, Hex>,
        checkBlocked: boolean = false,
        includeCenter: boolean = false,
    ): Map<string, number> {
        const result = new Map<string, number>();
        // const queue: { q: number, r: number, dist: number }[] = [
        //     { ...center, dist: 0 }
        // ];
        const centerId = coordsToId(center);
        const visited = new Set<string>([centerId]);

        const queue: { q: number, r: number, dist: number }[] = [{ q: center.q, r: center.r, dist: 0 }];
        let head = 0; // Вказівник на початок черги

        while (head < queue.length) {
            // const { q, r, dist } = queue.shift()!;
            const current = queue[head++]!; // O(1) замість O(N)
            const { q, r, dist } = current;
            const id = coordsToId({ q, r });
            const hex = battleMap.get(id);

            if (hex) {
                const isCenter = dist === 0;
                
                // Логіка включення:
                // 1. Якщо це центр, додаємо лише якщо includeCenter === true
                // 2. Якщо checkBlocked, ігноруємо зайняті клітинки (крім центру)
                const canBeAdded = isCenter 
                    ? includeCenter 
                    : (!checkBlocked || !hex.entityId);

                if (canBeAdded) {
                    result.set(id, dist);
                }
            }

            if (dist >= radius) continue;

            for (let i = 0; i < HEX_DIRECTIONS.length; i++) {
                const dir = HEX_DIRECTIONS[i]!;
                const nQ = q + dir.q;
                const nR = r + dir.r;
                
                // Тут відразу працюємо з nQ та nR
                const nId = coordsToId({ q: nQ, r: nR });
                const nHex = battleMap.get(nId);

                // Для прохідності (BFS) ми все ще маємо ігнорувати obstacles, 
                // але entityId ігноруємо лише при заповненні результату
                if (nHex && !nHex.isObstacle && !visited.has(nId)) {
                    visited.add(nId);
                    queue.push({ q: nQ, r: nR, dist: dist + 1 });
                }
            }
        }
        
        return result;
    }
    
    /**
     * Clear path cache
     * Call at turn start or when map changes
     */
    public static clearCache(): void {
        PathCalculator.pathCache.clear();
    }
    
    // ═══════════════════════════════════════════════════════════════════
    // PRIVATE HELPERS
    // ═══════════════════════════════════════════════════════════════════
    
    /**
     * A* pathfinding implementation
     */
    /**
     * Знаходить шлях з урахуванням вартості клітинок та небезпеки (ZoC).
     */
    public static computeAStarPath(
        startId: string,
        targetId: string,
        battleMap: Map<string, Hex>,
        zocMap?: Map<string, ZocNode>,
    ): string[] {
        const registry = ManagerRegistry.getInstance().entityRegistry;

        const start: { q: number; r: number; } = registry.getHex(startId)?.data ?? idToCoords(startId);
        const target: { q: number; r: number; } = registry.getHex(targetId)?.data ?? idToCoords(targetId);
        
        const parents = new Map<string, string | null>();
        const gScores = new Map<string, number>(); // Вартість від старту
        const fScores = new Map<string, number>(); // gScore + Heuristic
        const openSet: string[] = [startId];
        
        parents.set(startId, null);
        gScores.set(startId, 0);
        fScores.set(startId, getHexDistance(start, target));

        while (openSet.length > 0) {
            // Сортуємо за вже порахованим fScore. Це швидше, ніж рахувати дистанцію всередині sort
            openSet.sort((a, b) => {
                return (fScores.get(a) ?? Infinity) - (fScores.get(b) ?? Infinity);
            });

            const currentId = openSet.shift()!;

            // Якщо дійшли до цілі
            if (currentId === targetId) {
                const path: string[] = [];
                let tempId: string | null = currentId;
                
                // Відновлюємо шлях, але не включаємо startId (зазвичай в іграх шлях - це куди йти)
                while (tempId && tempId !== startId) {
                    path.push(tempId);
                    tempId = parents.get(tempId) || null;
                }
                return path.reverse();
            }

            const { q, r } = registry.getHex(currentId)?.data ?? idToCoords(currentId);
            
            for (const n of getAxialNeighbors(q, r)) {
                const nId = coordsToId(n);
                const hex = battleMap.get(nId);

                // Перевірка на прохідність:
                // 1. Гекс існує
                // 2. Не перешкода (стіна/прірва)
                // 3. Не зайнятий іншим юнітом (окрім цільового гекса або стартового)
                if (!hex || hex.isObstacle || (hex.entityId && nId !== startId && nId !== targetId)) {
                    continue;
                }

                // --- РОЗРАХУНОК ВАРТОСТІ (COST) ---
                
                // 1. Базова вартість переходу (зазвичай 1, або більше для лісу/болота)
                // Якщо у Hex немає поля cost, вважаємо 1
                // const baseCost = (hex as any).cost || 1; 
                const baseCost = TERRAINS[hex.terrain]?.moveCost ?? 10;

                // 2. Додаємо штраф за Zone of Control (Threat)
                let zocPenalty = 0;
                if (zocMap && zocMap.has(nId)) {
                    const damage = zocMap.get(nId)?.totalThreat || 0;
                    
                    // Скільки "умовних кроків" вартує цей ризик?
                    // Якщо damage = 10 (наш CV), то штраф буде рівно 1 baseCost.
                    // Якщо damage = 100, штраф все одно буде прагнути до 1 baseCost (через нормалізацію).
                    
                    const riskFactor = damage / (damage + UTILITY_CONSTANTS.CREATURE_VALUE); 
                    zocPenalty = riskFactor * baseCost; 
                }

                const moveCost = baseCost + zocPenalty;
                // ----------------------------------

                const tentativeG = (gScores.get(currentId) || 0) + moveCost;

                if (!gScores.has(nId) || tentativeG < gScores.get(nId)!) {
                    parents.set(nId, currentId);
                    gScores.set(nId, tentativeG);
                    const nIdPos = registry.getHex(nId)?.data ?? idToCoords(nId);
                    const heuristic = getHexDistance(nIdPos, target);
                    fScores.set(nId, tentativeG + heuristic);
                    
                    if (!openSet.includes(nId)) {
                        openSet.push(nId);
                    }
                }
            }
        }

        return []; // Шлях не знайдено
    }

    /**
     * Розрахунок вартості кроку з урахуванням террейну
     */
    public static getStepCost(speed: number, terrainMoveCost: number): number {
        const MOBILITY_PIVOT = 45;
        // 1. Конвертуємо Speed (1-5) у базову вартість (30, 25, 20, 15, 10).
        // Чим вище Speed, тим менше стаміни витрачається на 1 рівний гекс.
        const baseMoveCost = MOBILITY_PIVOT - (speed * 5); 
        
        // 2. Множимо на террейн (Наприклад: Flat = 1.0, Hill = 1.5, Swamp = 2.0)
        const rawCost = baseMoveCost * (terrainMoveCost / 10);
        const result = Math.round(rawCost / 5) * 5;
        // 3. Округлюємо до найближчого числа, кратного 5 (щоб уникнути 22.5 або 17)

        return Math.max(5, result)
        
    }

}